import * as React from "react";
import { Button, Card, Dialog, DialogContent } from "@jhool-io/fe-components";
import { useNavigate, useParams } from "react-router-dom";
import {
    downloadFormDocumentFile,
    useGetConsentByBookingId,
} from "../../hooks/client-forms.queries";
import {
    FormStatus,
    IConsentFormAnswers,
    IConsentFormRecord,
    IFormDocument,
} from "../../types/client-forms.types";
import ConsentStepper from "../ConsentStepper/ConsentStepper";
import useToast from "../../../../hooks/useToast";
import { useConsentAction } from "../../hooks/client-forms.mutations";
import {
    getConsentAttachmentFiles,
    isDraftSaveableStatus,
} from "../../helpers/client-forms.helpers";
import useCurrentClientProfile from "../../../../hooks/useCurrentClient";
import ConsentDocumentViewer from "../ConsentDocumentViewer/ConsentDocumentViewer";
import InfoCircleIcon from "../../../../components/Icons/InfoCircle";

export default function ConsentFormWrapper({
    initialConsent,
}: {
    initialConsent?: IConsentFormRecord;
}) {
    const [selectedDocument, setSelectedDocument] =
        React.useState<IFormDocument | null>(null);
    const [showSignatureDetails, setShowSignatureDetails] =
        React.useState(false);
    const params = useParams();
    const { toast } = useToast();
    const navigate = useNavigate();

    const { bookingId } = params;
    const currentClient = useCurrentClientProfile();
    const [downloadingDocumentId, setDownloadingDocumentId] = React.useState<
        string | null
    >(null);

    const { data } = useGetConsentByBookingId(bookingId ?? "", !initialConsent);

    const consent = initialConsent ?? data?.data;

    const consentAction = useConsentAction();
    const isDraftSaveable = consent
        ? isDraftSaveableStatus(consent.status)
        : false;
    const hasSubmitted =
        !isDraftSaveable &&
        (consent?.client_submitted ||
            consent?.status === FormStatus.RECEIVED ||
            consent?.status === FormStatus.COMPLETED);

    const formConfig = consent?.form_definition ?? null;
    const initialUploadedFiles = getConsentAttachmentFiles(
        consent?.consent_documents ?? []
    );

    const handleSubmit = async (
        answers: IConsentFormAnswers,
        emailCopyRequested: boolean
    ): Promise<void> => {
        if (!bookingId) {
            return;
        }

        try {
            await consentAction.mutateAsync({
                bookingId,
                data: {
                    action_type: "client_submitted",
                    email_copy_requested: emailCopyRequested,
                    form_answers: answers,
                },
            });
            toast({
                mode: "success",
                message: "Consent submitted successfully.",
            });
            navigate("/forms");
        } catch {
            const message =
                consentAction.error?.response?.data?.message ||
                "Could not submit consent. Please try again.";

            toast({
                mode: "error",
                message,
            });
        }
    };

    const handleDownload = async (document: IFormDocument): Promise<void> => {
        if (!bookingId) {
            return;
        }

        setDownloadingDocumentId(document.document_id);
        try {
            await downloadFormDocumentFile(document);
            consentAction.mutate({
                bookingId,
                data: { action_type: "client_downloaded" },
            });
        } catch {
            toast({
                mode: "error",
                message: "Could not download the consent document.",
            });
        } finally {
            setDownloadingDocumentId(null);
        }
    };

    return (
        <div className="p-32">
            <Dialog open={Boolean(selectedDocument)}>
                <DialogContent
                    variant="drawer"
                    title={
                        selectedDocument?.document_name || "Consent document"
                    }
                    handleCloseDialog={() => setSelectedDocument(null)}
                    showFooter={false}
                    wrapperClass="w-[1180px] max-w-[calc(100vw-24px)]"
                    classNames="!overflow-hidden !p-0"
                >
                    <div>
                        {selectedDocument && consent ? (
                            <ConsentDocumentViewer
                                consent={consent}
                                document={selectedDocument}
                                onDownload={() =>
                                    handleDownload(selectedDocument)
                                }
                                isDownloading={
                                    downloadingDocumentId ===
                                    selectedDocument.document_id
                                }
                            />
                        ) : null}
                    </div>
                </DialogContent>
            </Dialog>

            {formConfig && !hasSubmitted && (
                <div className="mb-12 flex items-start gap-6 rounded-r8 border border-stroke-subtle bg-surface-raised p-10">
                    <div className="mt-1 shrink-0">
                        <InfoCircleIcon />
                    </div>
                    <div className="min-w-0">
                        <p className="mb-2 text-sm font-semibold text-black">
                            About your electronic signature
                        </p>
                        <p className="text-xs leading-normal text-muted">
                            When you complete this form, we record your IP
                            address, device and browser type, and the date and
                            time you open the form, initial pages, and sign it.
                            We store this information with your signed form to
                            verify your identity and document when you signed.
                            It&apos;s stored securely as part of your record and
                            isn&apos;t used for marketing or shared beyond
                            what&apos;s needed for your care, billing or a legal
                            requirement.
                        </p>
                        <div className="mt-2 border-t border-stroke-subtle pt-2">
                            <Button
                                variant="normal"
                                aria-expanded={showSignatureDetails}
                                onClick={() =>
                                    setShowSignatureDetails(
                                        (visible) => !visible
                                    )
                                }
                                className="cursor-pointer border-0 bg-transparent p-0 text-xs font-semibold text-primary underline underline-offset-2"
                            >
                                What we collect and why
                            </Button>
                            {showSignatureDetails && (
                                <div className="mt-2 space-y-2 text-xs leading-normal text-muted">
                                    <p>
                                        Signing this form electronically has the
                                        same effect as signing it on paper. To
                                        make sure your signature can be
                                        verified, we record the following when
                                        you use this form:
                                    </p>
                                    <ul className="space-y-2 pl-16">
                                        <li>
                                            <span className="font-semibold text-black mr-1">
                                                Your IP address:
                                            </span>
                                            The network address your device uses
                                            to connect to the internet.
                                        </li>
                                        <li>
                                            <span className="font-semibold text-black mr-1">
                                                Your device and browser:
                                            </span>
                                            For example, “Chrome on iPhone.”
                                        </li>
                                        <li>
                                            <span className="font-semibold text-black mr-1">
                                                Date and time:
                                            </span>
                                            When you open the form, save
                                            progress, add attachments, initial
                                            or sign each page, submit, and
                                            download a copy.
                                        </li>
                                        <li>
                                            <span className="font-semibold text-black mr-1">
                                                What you entered:
                                            </span>
                                            Your initials, signatures,
                                            checkboxes, full name and dates on
                                            each page.
                                        </li>
                                        <li>
                                            <span className="font-semibold text-black mr-1">
                                                How you accessed the form:
                                            </span>
                                            The secure link or sign-in used to
                                            open it.
                                        </li>
                                    </ul>
                                    <p>
                                        <span className="font-semibold text-black mr-1">
                                            Why we collect it:
                                        </span>
                                        To confirm that you reviewed and signed
                                        each page, and the time. This creates a
                                        record that protects you and your
                                        provider if the consent ever needs to be
                                        verified.
                                    </p>
                                    <p>
                                        <span className="font-semibold text-black mr-1">
                                            How it’s used:
                                        </span>
                                        This information is stored with your
                                        signed form as part of your record. It’s
                                        not used for advertising and is only
                                        shared when needed for your care,
                                        billing or a legal requirement.
                                    </p>
                                    <p>
                                        You can download a copy of your signed
                                        form, or ask for one by email, when you
                                        submit.
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {consent?.status === FormStatus.NEEDS_CORRECTION && (
                <div className="mb-16 p-12 bg-[rgba(247,229,164,0.50)] rounded-md">
                    <p className="text-sm text-[#634D17]">
                        Corrections are required. Please review and resubmit.
                    </p>
                </div>
            )}

            <Card classNames="p-24 w-full">
                <div className="mb-20 pb-16 border-b border-strokelight">
                    <p className="text-[10px] uppercase text-[rgba(11,19,43,0.4)] mb-4">
                        Provider
                    </p>
                    <p className="text-sm font-medium">
                        {consent?.provider_details?.first_name}{" "}
                        {consent?.provider_details?.last_name}
                        {consent?.provider_details?.credential
                            ? ` · ${consent.provider_details.credential}`
                            : ""}
                    </p>
                    {consent?.ketamine_evaluator_details && (
                        <>
                            <p className="text-[10px] uppercase text-[rgba(11,19,43,0.4)] mt-12 mb-4">
                                NP / Evaluator
                            </p>
                            <p className="text-sm font-medium">
                                {consent.ketamine_evaluator_details.first_name}{" "}
                                {consent.ketamine_evaluator_details.last_name}
                                {consent.ketamine_evaluator_details.credential
                                    ? ` · ${consent.ketamine_evaluator_details.credential}`
                                    : ""}
                            </p>
                        </>
                    )}
                </div>
                {hasSubmitted && (
                    <div>
                        <p className="text-base font-semibold mb-8">
                            Consent submitted
                        </p>
                        <p className="text-sm text-[rgba(11,19,43,0.6)] mb-16">
                            Your consent form has been received. The team will
                            review it shortly.
                        </p>
                        {consent?.consent_documents?.length ? (
                            <div className="flex flex-col gap-8">
                                {consent.consent_documents.map((document) => (
                                    <Button
                                        key={document.document_id}
                                        variant="secondary"
                                        mode="outline"
                                        onClick={() =>
                                            setSelectedDocument(document)
                                        }
                                    >
                                        View {document.document_name}
                                    </Button>
                                ))}
                            </div>
                        ) : (
                            <p className="text-sm text-[rgba(11,19,43,0.5)]">
                                The submitted document is not available to view
                                yet.
                            </p>
                        )}
                    </div>
                )}

                {formConfig && !hasSubmitted ? (
                    <ConsentStepper
                        consentId={consent?.consent_id}
                        brand={formConfig.brand}
                        formType={consent?.consent_form_type}
                        variant={formConfig.variant}
                        clientFullName={`${currentClient?.firstName ?? ""} ${currentClient?.lastName ?? ""}`.trim()}
                        clientDateOfBirth={currentClient?.dateOfBirth ?? ""}
                        pages={formConfig.pages}
                        initialDraftData={consent?.draft_data}
                        isSubmitting={consentAction.isLoading}
                        onSubmit={handleSubmit}
                        paymentCardStepConfig={formConfig.paymentCardStep}
                        attachmentsStepConfig={formConfig.attachmentsStep}
                        initialUploadedFiles={initialUploadedFiles}
                    />
                ) : null}
            </Card>
        </div>
    );
}
